import { memo, useMemo, useState } from "react";
import {
  AccountData,
  Event,
  Follower,
  ProfileHomePageId,
  ProfilePageSection,
} from "@markit/common.types";
import ProfilePageUserSubscriptions from "./ProfilePageUserSubscriptions";
import ProfilePageTimelineSection from "./ProfilePageTimelineSection";
import { Colors } from "../../utils/colors";
import { Icon } from "@iconify/react";
import EmptyStateButton from "../Buttons/EmptyStateButton";
import {
  isEventRelatedType,
  isProductSpecificType,
} from "@markit/common.utils";

type ProfilePageSubscriberProps = {
  userData: AccountData;
  followingAccountDataFollowing: AccountData[];
  tabItem: string;
  eventOrganizers: AccountData[];
  userEvents: Event[];
  userAttendingEvents: Event[];
  loadingEvents: boolean;
};

const ProfilePageSubscriber = memo(function ProfilePageSubscriberFn(
  props: ProfilePageSubscriberProps
) {
  const {
    userData,
    followingAccountDataFollowing,
    tabItem,
    eventOrganizers,
    userEvents,
    userAttendingEvents,
    loadingEvents,
  } = props;
  const [followerDataMap, setFollowerDataMap] = useState<Map<string, Follower>>(
    new Map()
  );
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(true);

  const isHomePage = useMemo(() => tabItem === ProfileHomePageId, [tabItem]);

  const hasMarkitTickets = useMemo(
    () =>
      userAttendingEvents.some((event) => isEventRelatedType(event.eventType)),
    [userAttendingEvents]
  );

  const hasMarkitOrders = useMemo(
    () => userEvents.some((event) => isProductSpecificType(event.eventType)),
    [userEvents]
  );

  if (
    isHomePage
      ? !loadingEvents &&
        userAttendingEvents.length === 0 &&
        userEvents.length === 0
      : !loadingSubscriptions && followerDataMap.size === 0
  ) {
    return (
      <EmptyStateButton
        title={"Nothing to see here"}
        description={
          isHomePage
            ? "Your purchases will show up here"
            : "Your subscriptions will show up here"
        }
        icon={
          <Icon
            icon={isHomePage ? "ion:home" : "ion:ribbon"}
            height={34}
            style={{ color: Colors.GRAY1 }}
          />
        }
        iconBox={48}
        iconBackgroundColor={Colors.WHITE1}
        containerStyles={{ paddingBlock: 60 }}
      />
    );
  }

  return (
    <div>
      {isHomePage ? (
        <div className="ColumnNormal" style={{ gap: 24 }}>
          {hasMarkitTickets ? (
            <ProfilePageTimelineSection
              userData={userData}
              profilePageSection={ProfilePageSection.EVENTS_CALENDAR}
              eventOrganizers={eventOrganizers}
              userEvents={userEvents}
              userAttendingEvents={userAttendingEvents}
              loadingEvents={loadingEvents}
            />
          ) : null}
          {hasMarkitOrders ? (
            <ProfilePageTimelineSection
              userData={userData}
              profilePageSection={ProfilePageSection.PRODUCTS_SECTION}
              eventOrganizers={eventOrganizers}
              userEvents={userEvents}
              userAttendingEvents={userAttendingEvents}
              loadingEvents={loadingEvents}
            />
          ) : null}
        </div>
      ) : (
        <ProfilePageUserSubscriptions
          followingAccountDataFollowing={followingAccountDataFollowing}
          followerDataMap={followerDataMap}
          setFollowerDataMap={setFollowerDataMap}
          loading={loadingSubscriptions}
          setLoading={setLoadingSubscriptions}
        />
      )}
    </div>
  );
});

export default ProfilePageSubscriber;
